/**
 * Vincent B1028 (Eric 2026-02-26 SPEC STRICK PROD) — Composant générique
 * de menu d'actions pour lignes de vidéo dans la File d'attente.
 *
 * Extrait le moteur commun des dropdowns « Reprendre... » (B1026) et
 * « Sortir de la file... » (B1028). Un seul point de vérité pour :
 *   - la gestion click-outside + Escape,
 *   - le rendu du bouton principal (icône + libellé + chevron + busy state),
 *   - le rendu du panneau avec items (label + description + icône + testid),
 *   - l'accessibilité ARIA (haspopup, expanded, role menu/menuitem).
 *
 * L'ordre des items est celui du tableau `items` reçu — le composant ne
 * réordonne rien (c'est aux wrappers spécifiques de trier par
 * destructivité croissante pour éviter les fat-fingers).
 */
import { useEffect, useRef, useState } from "react";
import { useLang } from "../../contexts/LanguageContext";
import { CaretDown, SpinnerGap } from "@phosphor-icons/react";


export default function ActionMenu({
  video,
  busy,
  items,                // Array<{ id, label, desc, onClick, busyKey, danger?, icon, empeche? }>
  triggerLabel,         // "Reprendre" | "Sortir de la file"
  triggerIcon: TriggerIcon,
  triggerClass,         // "bg-blue-600 ..." (bouton principal)
  testidPrefix,         // "queue-resume-menu" | "queue-exit-menu"
  // Tâche 1493 — `onOpen` laisse le menu charger son contenu AU CLIC. L'état
  // réel des étapes coûte quatre lectures par vidéo : le charger pour les
  // 200 lignes de l'Explorateur aurait ralenti l'écran pour un menu qu'on
  // n'ouvre qu'une fois. `sizeClass` permet au même menu de tenir dans une
  // ligne de liste (texte 10 px) comme dans le bandeau du pipeline.
  onOpen,
  sizeClass = "px-2.5 py-1.5 text-xs",
  disabled = false,
  triggerTitle,
}) {
  const { tr } = useLang();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const anyBusy = !!busy;
  const busyLabel = items.find((it) => it.busyKey === busy)?.label;

  if (!items.length) return null;

  return (
    <div ref={rootRef} className="relative">
      <button
        onClick={() => {
          setOpen((v) => {
            const suivant = !v;
            if (suivant && onOpen) onOpen();
            return suivant;
          });
        }}
        disabled={anyBusy || disabled}
        className={`${sizeClass} rounded-lg flex items-center gap-1.5 disabled:opacity-50 font-semibold ${triggerClass}`}
        data-testid={`${testidPrefix}-${video.id}`}
        title={triggerTitle || busyLabel || `${triggerLabel} ${tr("(choisir l'action)", "(choose the action)")}`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {anyBusy ? (
          <SpinnerGap size={14} className="animate-spin" />
        ) : (
          <TriggerIcon size={14} weight="fill" />
        )}
        {anyBusy ? busyLabel || `${triggerLabel}…` : triggerLabel}
        {!anyBusy && <CaretDown size={12} weight="bold" />}
      </button>

      {open && !anyBusy && (
        <div
          role="menu"
          data-testid={`${testidPrefix}-panel-${video.id}`}
          className="absolute right-0 top-full mt-1 z-30 w-80 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-lg shadow-lg p-1"
        >
          {items.map((it) => {
            const ItemIcon = it.icon;
            const dangerCls = it.danger
              ? "hover:bg-red-50 dark:hover:bg-red-950/40 text-red-700 dark:text-red-300 border-t border-zinc-100 dark:border-zinc-800 mt-1 pt-2"
              : "hover:bg-zinc-100 dark:hover:bg-zinc-800";
            // Tâche 1493 — Une option sans objet (« tout est déjà indexé »)
            // reste VISIBLE mais inerte, avec sa raison : la masquer laisserait
            // croire que l'application a perdu une capacité.
            const inerte = !!it.empeche;
            return (
              <button
                key={it.id}
                role="menuitem"
                disabled={inerte}
                onClick={() => { setOpen(false); it.onClick(); }}
                data-testid={`${testidPrefix}-item-${it.id}-${video.id}`}
                data-inerte={inerte ? "true" : "false"}
                title={it.empeche || undefined}
                className={`w-full text-left px-3 py-2 rounded-md flex items-start gap-2 ${inerte ? "opacity-50 cursor-not-allowed" : dangerCls}`}
              >
                {ItemIcon && (
                  <ItemIcon
                    size={14}
                    weight="fill"
                    className={`mt-0.5 shrink-0 ${it.danger ? "text-red-600 dark:text-red-400" : "text-blue-600 dark:text-blue-400"}`}
                  />
                )}
                <div className="min-w-0">
                  <div className={`text-xs font-semibold ${it.danger ? "text-red-700 dark:text-red-300" : "text-zinc-900 dark:text-zinc-100"}`}>{it.label}</div>
                  <div className="text-[11px] text-zinc-600 dark:text-zinc-400 leading-snug mt-0.5">{it.empeche || it.desc}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
